// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { formatCurrency as formatCurrencyUtil } from '@/lib/utils';

export interface BidIncrement {
  min_amount: number;
  max_amount: number | null;
  increment: number;
}

export function calculateNextMinimumBid(
  currentBid: number,
  increments: BidIncrement[]
): number {
  const applicableIncrement = increments.find(
    inc => currentBid >= inc.min_amount && 
           (inc.max_amount === null || currentBid < inc.max_amount)
  );
  
  if (!applicableIncrement) {
    // Default to 10% if no increment rule found
    return Math.ceil(currentBid * 1.1);
  }
  
  return currentBid + applicableIncrement.increment;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function validateBidAmount(
  amount: number,
  minimumBid: number,
  userMaxBudget?: number
): { isValid: boolean; error?: string } {
  if (amount < minimumBid) {
    return {
      isValid: false,
      error: `Minimum bid is ${formatCurrency(minimumBid)}`
    };
  }
  
  if (userMaxBudget && amount > userMaxBudget) {
    return {
      isValid: false,
      error: `Amount exceeds your maximum budget of ${formatCurrency(userMaxBudget)}`
    };
  }
  
  return { isValid: true };
}

export interface OptimizedBidSubmissionProps {
  lotId: string;
  userId: string;
  amount: number;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onComplete?: () => void;
  skipEmailNotification?: boolean;
}

/**
 * Optimized bid submission with immediate feedback
 * This function provides instant user feedback while processing heavy operations in the background
 */
export const submitBidOptimized = async ({
  lotId,
  userId,
  amount,
  onSuccess,
  onError,
  onComplete,
  skipEmailNotification = false
}: OptimizedBidSubmissionProps): Promise<{ success: boolean; message?: string }> => {
  try {
    // **CRITICAL PATH** - Submit bid immediately for fast UX
    const { error: bidError } = await supabase
      .from("bids")
      .insert({
        lot_id: lotId,
        user_id: userId,
        amount: amount,
        status: 'pending' // Let admin approval handle current_bid updates
      } as any);

    if (bidError) {
      const errorMessage = bidError.message || 'Failed to submit bid';
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      onError?.(errorMessage);
      return { success: false, message: errorMessage };
    }

    // **IMMEDIATE SUCCESS FEEDBACK** - Show success immediately
    toast({
      title: "Bid Submitted ✅",
      description: `Your bid of ${formatCurrency(amount)} has been submitted and is pending approval.`,
    });

    onSuccess?.();

    // **BACKGROUND PROCESSING** - Handle heavy operations without blocking UI
    if (!skipEmailNotification) {
      setTimeout(async () => {
        try {
          // Send email notification in background
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, email')
            .eq('id', userId)
            .single();

          if (profile) {
            const profileData = profile as any;
            const userName = `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || 'Valued Customer';
            
            // Get lot information for email
            const { data: lotData } = await supabase
              .from('lots')
              .select(`
                lot_number,
                current_bid,
                starting_bid,
                artworks (
                  title,
                  image_url,
                  artist (name)
                ),
                auctions (title)
              `)
              .eq('id', lotId)
              .single();

            if (lotData) {
              const lot = lotData as any;
              const artwork = lot.artworks;
              const auction = lot.auctions;
              
              // Send email using existing service
              const { EmailNotificationService } = await import('@/services/emailNotificationService');
              
              await EmailNotificationService.sendBidConfirmationNotification({
                userEmail: profileData.email || '',
                userId,
                userName,
                artworkTitle: artwork?.title || `Lot #${lot.lot_number}`,
                artistName: artwork?.artist?.name || 'Unknown Artist',
                lotNumber: lot.lot_number?.toString() || '',
                bidAmount: amount,
                currentHighBid: lot.current_bid || lot.starting_bid || 0,
                auctionTitle: auction?.title || 'Florida Highwaymen Auction',
                auctionEndTime: 'TBD',
                lotId,
                artworkImage: artwork?.image_url || undefined
              });

              console.log('✅ Background: Bid confirmation email sent successfully');
            }
          }
        } catch (emailError) {
          console.error('❌ Background: Failed to send bid confirmation email:', emailError);
          // Don't show error toast for background operations
        }
      }, 100); // Small delay to ensure UI updates first
    }

    return { success: true };
    
  } catch (error: any) {
    const errorMessage = error.message || 'Failed to submit bid';
    
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
    
    onError?.(errorMessage);
    return { success: false, message: errorMessage };
    
  } finally {
    onComplete?.();
  }
};

/**
 * Quick bid validation without heavy database operations
 */
export const validateBidQuick = async (lotId: string, bidAmount: number): Promise<{
  valid: boolean;
  message?: string;
  minimumBid?: number;
}> => {
  try {
    // Get basic lot information for validation
    const { data: lot, error } = await supabase
      .from('lots')
      .select('current_bid, starting_bid, reserve_price')
      .eq('id', lotId)
      .single();

    if (error || !lot) {
      return { valid: false, message: 'Could not validate bid - lot not found' };
    }

    const startingBid = Number((lot as any).starting_bid) || 0;
    const currentBid = (lot as any).current_bid ? Number((lot as any).current_bid) : null;

    console.log(`🔍 Validating bid: $${bidAmount} | Starting: $${startingBid} | Current: ${currentBid ? `$${currentBid}` : 'none'}`);

    // Check if there are actual bids in the database
    const { data: platformBids } = await supabase
      .from('bids')
      .select('id')
      .eq('lot_id', lotId)
      .eq('status', 'approved')
      .limit(1);
      
    const { data: manualBids } = await supabase
      .from('manual_bids')
      .select('id')
      .eq('lot_id', lotId)
      .limit(1);
      
    const hasActualBids = (platformBids && platformBids.length > 0) || (manualBids && manualBids.length > 0);

    // If current_bid equals starting_bid and there are no actual bids, treat as first bid
    if (currentBid === startingBid && !hasActualBids) {
      if (bidAmount < startingBid) {
        return {
          valid: false,
          message: `Minimum bid is ${formatCurrency(startingBid)}`,
          minimumBid: startingBid
        };
      }
      console.log(`✅ First bid validation passed: $${bidAmount} >= $${startingBid} (no actual bids yet)`);
      return { valid: true };
    }

    // If no current bid exists or current_bid < starting_bid, first bid must be at least the starting bid
    if (!currentBid || currentBid < startingBid) {
      if (bidAmount < startingBid) {
        return {
          valid: false,
          message: `Minimum bid is ${formatCurrency(startingBid)}`,
          minimumBid: startingBid
        };
      }
      console.log(`✅ First bid validation passed: $${bidAmount} >= $${startingBid}`);
      return { valid: true };
    }

    // Current bid exists and is higher than starting bid - prevent decreasing bids and enforce increments
    if (bidAmount <= currentBid) {
      return {
        valid: false,
        message: `Bid must be higher than current bid of ${formatCurrency(currentBid)}`,
        minimumBid: currentBid + 1
      };
    }

    console.log(`✅ Increment bid validation passed: $${bidAmount} > $${currentBid}`);
    return { valid: true };
    
  } catch (error) {
    console.error('Error validating bid:', error);
    return { valid: false, message: 'Error validating bid' };
  }
};

/**
 * Get minimum next bid for a lot
 */
export const getMinimumNextBid = async (lotId: string): Promise<number> => {
  try {
    const { data: lot } = await supabase
      .from('lots')
      .select('current_bid, starting_bid')
      .eq('id', lotId)
      .single();

    if (!lot) return 0;

    const startingBid = Number((lot as any).starting_bid) || 0;
    const currentBid = (lot as any).current_bid ? Number((lot as any).current_bid) : null;

    console.log(`🎯 Getting minimum bid | Starting: $${startingBid} | Current: ${currentBid ? `$${currentBid}` : 'none'}`);

    // If no current bid, minimum is exactly the starting bid
    if (!currentBid || currentBid < startingBid) {
      console.log(`📍 No current bid - minimum is starting bid: $${startingBid}`);
      return startingBid;
    }

    // Current bid exists - apply increments using the correct bid increment logic
    const nextMinimum = await getNextBidAmount(currentBid, lotId);
    console.log(`💰 Current bid exists - next minimum: $${nextMinimum}`);
    return nextMinimum;
    
  } catch (error) {
    console.error('Error getting minimum bid:', error);
    return 0;
  }
};
